import { useState, useRef, } from 'react';
import { useAsync, } from 'react-use';
import { isEmpty, chunk, } from 'lodash';
import { format as formatDate, addMonths, } from 'date-fns';

import { getCollectionData, getDocumentData, } from '../../shared/firebase';
import { computeSegmentsAmounts, computeCompaniesAmounts, } from '../../shared/models/consolidationAccountItem';

// NOTE: setTimeout はバックグラウンドタブで最短1秒に間引かれ、月数×1秒遅くなるため MessageChannel を使う
const yieldToBrowser = () => new Promise((resolve) => {
  const { port1, port2, } = new MessageChannel();
  port1.onmessage = () => resolve();
  port2.postMessage(null);
});

export default function useMonthlySegmentsAmounts (company, relatedCompanies, allConsolidationJournalTypes, allConsolidationAccountItems, accountItems, sections, allConsolidationSegments, yearMonthDates) {
  const [progress, setProgress] = useState({ fetchedCount: 0, fetchTotal: 0, computedCount: 0, computeTotal: 0, });
  const runIdRef = useRef(0);
  const computeMonthItems = async () => {
    if(isEmpty(yearMonthDates) || !relatedCompanies || !allConsolidationJournalTypes || !allConsolidationAccountItems || isEmpty(accountItems) || !sections || !allConsolidationSegments) return;

    const runId = runIdRef.current + 1;
    runIdRef.current = runId;
    const isCurrentRun = () => runIdRef.current === runId;
    const updateProgress = f => isCurrentRun() && setProgress(f);
    updateProgress({ fetchedCount: 0, fetchTotal: yearMonthDates.length + (company.usesMonthlyAr ? 1 : 0), computedCount: 0, computeTotal: yearMonthDates.length, });
    const getData = async (yearMonthDate) => {
      const yearMonth = formatDate(yearMonthDate, 'yyyyMM');
      const [trials, sectionTrials, individualAdjustmentJournals, consolidationJournals, exchangedItems, exchangeRate] = await Promise.all([
        getCollectionData(company.ref.collection('trials').where('yearMonth', '==', yearMonth)),
        getCollectionData(company.ref.collection('sectionTrials').where('yearMonth', '==', yearMonth)),
        getCollectionData(company.ref.collection('individualAdjustmentJournals').where('yearMonth', '==', yearMonth)),
        getCollectionData(company.ref.collection('consolidationJournals').where('yearMonth', '==', yearMonth)),
        getCollectionData(company.ref.collection('exchangedItems').where('yearMonth', '==', yearMonth)),
        getDocumentData(company.ref.collection('exchangeRates').doc(yearMonth)),
      ]);
      updateProgress(_ => ({ ..._, fetchedCount: _.fetchedCount + 1 }));
      return { yearMonth, trials, sectionTrials, individualAdjustmentJournals, consolidationJournals, exchangedItems, exchangeRate };
    };
    const [allMonthGroups, prevMonthGroup] = await Promise.all([
      // NOTE: 全月を同時に取ると受信データの処理でメインスレッドが埋まり、進捗が全く進まないまま一気に終わるため、数ヶ月ずつ取る
      chunk(yearMonthDates, 3).reduce(async (x, yearMonthDates) => {
        const monthGroups = await x;
        return isCurrentRun() ? [...monthGroups, ...(await Promise.all(yearMonthDates.map(getData)))] : monthGroups;
      }, Promise.resolve([])),
      company.usesMonthlyAr ? getData(addMonths(yearMonthDates[0], -1)) : null,
    ]);
    // NOTE: 1ヶ月ごとに描画へ処理を返さないと、集計中は画面が固まり進捗も更新されない
    const monthItems = await yearMonthDates.reduce(async (x, yearMonthDate, i) => {
      const monthItems = await x;
      await yieldToBrowser();
      if(!isCurrentRun()) return monthItems;

      const monthGroups = company.usesMonthlyAr ? allMonthGroups.slice(0, i + 1) : [allMonthGroups[i]];
      const monthItem = {
        yearMonth: formatDate(yearMonthDate, 'yyyyMM'),
        items: computeSegmentsAmounts(company, relatedCompanies, allConsolidationAccountItems, accountItems, sections, allConsolidationSegments, monthGroups, prevMonthGroup),
        consolidationCalculationItems: computeCompaniesAmounts(company, relatedCompanies, allConsolidationJournalTypes, allConsolidationAccountItems, accountItems, monthGroups, prevMonthGroup, undefined, false, true),
      };
      updateProgress(_ => ({ ..._, computedCount: _.computedCount + 1 }));
      return [...monthItems, monthItem];
    }, Promise.resolve([]));
    await yieldToBrowser();
    return monthItems;
  };
  const { value: monthItems, loading: isLoading, error, } = useAsync(computeMonthItems, [company, relatedCompanies, allConsolidationJournalTypes, allConsolidationAccountItems, accountItems, sections, allConsolidationSegments, yearMonthDates]);
  if(error) throw error;

  return { monthItems, isLoading, progress, };
};
